import { Flex, InputNumber } from "antd";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { formatTranslation } from "../../../../shared/utils/translationUtils";
import { restrictToTwoDecimals } from "../../utils";
import { DynamicDensityInputs } from "./DynamicDensityInputs";

interface DensityRangeBaseProps {
  globalDensityRange: boolean;
  updateProductDensityRange: (
    productId: string,
    densityType: "max" | "min",
    value: number
  ) => void;

  globalDensityValues?: { min?: number | null; max?: number | null };

  productDensityRangeMax?: number;
  productDensityRangeMin?: number;
  productId: string;
  productName?: string;
  hideInputLabels?: boolean;
}

type DensityRangeModeProps = DensityRangeBaseProps & {
  densityMode?: "range";

  productDensityOptions?: number[];
  updateProductDensityOptions?: (productId: string, values: number[]) => void;
};

type DensityOptionsModeProps = DensityRangeBaseProps & {
  densityMode: "options";
  productDensityOptions?: number[];
  updateProductDensityOptions: (productId: string, values: number[]) => void;
};

type DensityRangeComponentProps =
  | DensityRangeModeProps
  | DensityOptionsModeProps;

const normalizeDensityOptions = (values: number[]): number[] =>
  Array.from(new Set(values.filter((n) => Number.isFinite(n)))).sort(
    (a, b) => a - b
  );

export const DensityRangeComponent = ({
  globalDensityRange,
  updateProductDensityRange,
  globalDensityValues,
  productDensityRangeMax,
  productDensityRangeMin,
  productId,
  productName,
  productDensityOptions = [],
  updateProductDensityOptions,
  densityMode = "range",
  hideInputLabels = false,
}: DensityRangeComponentProps): JSX.Element => {
  const { t } = useTranslation();

  const [minValue, setMinValue] = useState<number | null>(
    globalDensityRange
      ? globalDensityValues?.min ?? null
      : productDensityRangeMin ?? null
  );

  const [maxValue, setMaxValue] = useState<number | null>(
    globalDensityRange
      ? globalDensityValues?.max ?? null
      : productDensityRangeMax ?? null
  );

  useEffect(() => {
    setMinValue(
      globalDensityRange
        ? globalDensityValues?.min ?? null
        : productDensityRangeMin ?? null
    );
    setMaxValue(
      globalDensityRange
        ? globalDensityValues?.max ?? null
        : productDensityRangeMax ?? null
    );
  }, [
    globalDensityRange,
    globalDensityValues,
    productDensityRangeMin,
    productDensityRangeMax,
  ]);

  const hasError =
    minValue !== null && maxValue !== null && minValue > maxValue;

  const handleDensityOptionsChangeUnsorted = (
    nextProductId: string,
    values: number[]
  ): void => {
    if (typeof updateProductDensityOptions !== "function") return;
    updateProductDensityOptions(nextProductId, values);
  };

  const handleDensityOptionsBlurSort = (
    nextProductId: string,
    values: number[]
  ): void => {
    if (typeof updateProductDensityOptions !== "function") return;
    const normalized = normalizeDensityOptions(values);
    updateProductDensityOptions(nextProductId, normalized);
  };

  const normalizedInitialValues = useMemo(
    () => normalizeDensityOptions(productDensityOptions),
    [productDensityOptions]
  );

  return (
    <div>
      {densityMode === "range" && (
        <>
          {globalDensityRange && (
            <div>
              {formatTranslation(t("createProtocol.products.densityByRange"))}
            </div>
          )}
          <Flex gap={10} wrap="wrap">
            <div>
              {!hideInputLabels && (
                <div>{formatTranslation(t("createProtocol.products.min"))}</div>
              )}
              <InputNumber
                placeholder="0"
                min={1}
                max={999999}
                step={0.01}
                precision={2}
                value={minValue}
                className="basicInfo-input-num"
                onChange={(value) => {
                  if (typeof value !== "number") return;
                  setMinValue(value);
                  updateProductDensityRange(productId, "min", value);
                }}
                onKeyDown={restrictToTwoDecimals}
                status={hasError ? "error" : undefined}
              />
            </div>

            <div>
              {!hideInputLabels && (
                <div>{formatTranslation(t("createProtocol.products.max"))}</div>
              )}
              <InputNumber
                placeholder="0"
                min={1}
                max={999999}
                step={0.01}
                precision={2}
                value={maxValue}
                className="basicInfo-input-num"
                onChange={(value) => {
                  if (typeof value !== "number") return;
                  setMaxValue(value);
                  updateProductDensityRange(productId, "max", value);
                }}
                onKeyDown={restrictToTwoDecimals}
                status={hasError ? "error" : undefined}
              />
            </div>
          </Flex>

          {hasError && (
            <div
              style={{ color: "#ff4d4f", fontSize: "12px", marginTop: "4px" }}
            >
              {formatTranslation(
                t("createProtocol.products.nutrients.minLowerThanMaxRate.error")
              )}
            </div>
          )}
        </>
      )}

      {densityMode === "options" && (
        <>
          <div>
            {formatTranslation(t("createProtocol.products.densityByOptions"))}
          </div>
          <DynamicDensityInputs
            productId={productId}
            productName={productName}
            onDensityValuesChange={handleDensityOptionsChangeUnsorted}
            onDensityValuesBlur={handleDensityOptionsBlurSort}
            initialValues={normalizedInitialValues}
          />
        </>
      )}
    </div>
  );
};
